<script setup>
import ImportEmailingAutomationEmailExclusionsAction from '@frontJs/components/actions/projectManagement/modules/ImportEmailingAutomationEmailExclusionsAction.vue';
import TrayArrowDownIcon from '@iconify-vue/ph/cloud-arrow-down';
import TrashIcon from '@iconify-vue/ph/trash';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastDeleted } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const { tenant, emailingAutomation } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    emailingAutomation: {
        type: Object,
        required: false,
    },
});

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'value',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.value,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(
                                DeleteAction,
                                {
                                    url: route(
                                        'front.projectManagement.modules.deleteEmailingAutomationEmailExclusion',
                                        {
                                            tenant: tenant.id,
                                            emailingAutomationEmailExclusion:
                                                props.row.original.id,
                                        },
                                    ),
                                    callback: () => {
                                        toastDeleted();

                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            icon: TrashIcon,
                                            size: 'small',
                                            type: 'secondary',
                                            title: 'Supprimer',
                                            square: true,
                                        }),
                                },
                            ),
                    },
                ),
        }),
    ],
    url: route(
        'front.projectManagement.modules.postEmailingAutomationEmailExclusions',
        {
            tenant: tenant.id,
        },
    ),
    data: {
        emailingAutomationId: emailingAutomation ? emailingAutomation.id : null,
    },
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ImportEmailingAutomationEmailExclusionsAction
                :tenant="tenant"
                :emailing-automation="emailingAutomation"
                :callback="updateData"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        :left-icon="TrayArrowDownIcon"
                        type="primary"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportEmailingAutomationEmailExclusionsAction>
        </template>
    </DataTableLayout>
</template>
